import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Truck, MessageCircle, Wrench, RefreshCcw, ArrowRight, Star } from "lucide-react";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import VehicleFinder from "@/components/VehicleFinder";
import ProductCard from "@/components/ProductCard";
import KlarnaBanner from "@/components/KlarnaBanner";
import BrandsGrid from "@/components/BrandsGrid";
import TopModelsGrid from "@/components/TopModelsGrid";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { HOME } from "@/constants/testIds";
import SEO, { buildOrganizationLd, buildWebsiteLd } from "@/components/SEO";

const WHY_KEYS = [
  { icon: ShieldCheck, tKey: "why_1" },
  { icon: MessageCircle, tKey: "why_2" },
  { icon: Wrench, tKey: "why_3" },
  { icon: RefreshCcw, tKey: "why_4" },
];

const STEP_KEYS = [
  { n: "01", tKey: "step_1" },
  { n: "02", tKey: "step_2" },
  { n: "03", tKey: "step_3" },
];

// Public URLs for the "View all reviews" CTAs at the bottom of the carousel.
const REVIEWS_FACEBOOK_URL = "https://www.facebook.com/profile.php?id=61592341931486&sk=reviews";
const REVIEWS_TRUSTPILOT_URL = "https://it.trustpilot.com/review/audrivio.com";

// Badge styling per review source. `icon` is the inline SVG rendered inside
// the pill. `label` is the visible text. `badge` is the Tailwind bg class.
const REVIEW_SOURCES = {
  facebook: {
    label: "Facebook",
    badge: "bg-[#1877F2]",
    icon: (<svg viewBox="0 0 24 24" className="w-3 h-3" fill="currentColor" aria-hidden><path d="M22 12a10 10 0 1 0-11.6 9.9v-7H8V12h2.4V9.8c0-2.4 1.4-3.7 3.6-3.7 1 0 2.1.2 2.1.2v2.3h-1.2c-1.2 0-1.5.7-1.5 1.5V12h2.6l-.4 2.9h-2.2V22A10 10 0 0 0 22 12z"/></svg>),
  },
  trustpilot: {
    label: "Trustpilot",
    badge: "bg-[#00B67A]",
    icon: (<svg viewBox="0 0 24 24" className="w-3 h-3" fill="currentColor" aria-hidden><path d="M12 2l2.6 7.6h8L16.1 14l2.6 7.6L12 17l-6.7 4.6L7.9 14l-6.5-4.4h8L12 2z"/></svg>),
  },
  google: {
    label: "Google",
    badge: "bg-[#EA4335]",
    icon: (<svg viewBox="0 0 24 24" className="w-3 h-3" fill="currentColor" aria-hidden><path d="M12 10.2v3.9h5.5a5 5 0 0 1-2.2 3.3v2.7h3.6c2.1-1.9 3.3-4.8 3.3-8.2 0-.8-.1-1.5-.2-2.2H12z"/><path d="M12 22c3 0 5.4-1 7.2-2.7l-3.5-2.7c-1 .7-2.2 1.1-3.7 1.1-2.9 0-5.3-1.9-6.2-4.5H2.2v2.8A10 10 0 0 0 12 22z"/><path d="M5.8 13.2a6 6 0 0 1 0-3.8V6.6H2.2a10 10 0 0 0 0 10.8l3.6-2.8z"/><path d="M12 5.8c1.6 0 3 .5 4.2 1.5l3.2-3.2A10 10 0 0 0 12 2a10 10 0 0 0-9.8 6.6l3.6 2.8C6.7 7.6 9 5.8 12 5.8z"/></svg>),
  },
};

export default function Home() {
  const [homeContent, setHomeContent] = useState(null);
  const [best, setBest] = useState([]);
  const [sale, setSale] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [latestPosts, setLatestPosts] = useState([]);
  const { t, lang } = useLang();

  useEffect(() => {
    api.get("/homepage").then((r) => setHomeContent(r.data));
    api.get("/products/bestsellers", { params: { limit: 4 } }).then((r) => setBest(r.data)).catch(() => setBest([]));
    api.get("/products", { params: { badge: "sale" } }).then((r) => setSale(r.data.slice(0, 4)));
    api.get("/reviews").then((r) => setReviews((r.data || []).slice(0, 12)));
    api.get("/blog").then((r) => setLatestPosts((r.data || []).slice(0, 3))).catch(() => setLatestPosts([]));
  }, []);

  return (
    <div className="min-h-screen bg-xd-light">
      <SEO
        title={lang === "it" ? "Autoradio Android Plug&Play per la tua auto" : lang === "en" ? "Plug&Play Android head units for your car" : lang === "fr" ? "Autoradios Android Plug&Play pour votre voiture" : lang === "de" ? "Plug&Play Android-Autoradios für Ihr Auto" : "Autorradios Android Plug&Play para tu coche"}
        description={lang === "it" ? "Autoradio Android su misura per Fiat, VW, Audi, BMW, Mercedes e altre marche. Plug&Play, installazione in 30 minuti, garanzia 24 mesi, spedizione 24-48h." : lang === "en" ? "Android head units tailored for Fiat, VW, Audi, BMW, Mercedes and more. Plug&Play, 30-minute install, 24-month warranty, 24-48h shipping." : undefined}
        path="/"
        lang={lang}
        type="website"
        jsonLd={[buildOrganizationLd(), buildWebsiteLd()]}
        hreflangs={{ it: "/", en: "/", fr: "/", de: "/", es: "/" }}
      />
      <Header />

      {/* HERO */}
      <section className="relative bg-xd-dark text-white overflow-hidden">
        <div className="absolute inset-0 opacity-40" style={{ background: "radial-gradient(1000px 500px at 80% 10%, rgba(23,199,214,0.25), transparent 55%), radial-gradient(700px 400px at 10% 90%, rgba(255,122,41,0.15), transparent 60%)" }} />
        <div className="grain" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-16 lg:py-24 grid lg:grid-cols-2 gap-12 items-center">
          <div className="animate-fade-up">
            <span className="inline-flex items-center gap-2 rounded-full bg-white/5 border border-white/10 px-3 py-1 text-xs text-xd-cyan font-mono-tech uppercase tracking-widest">
              <span className="w-1.5 h-1.5 rounded-full bg-xd-cyan animate-pulse" />
              {(lang === "it" && homeContent?.hero_badge) || t("hero_badge")}
            </span>
            <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] font-semibold tracking-tight">
              {(lang === "it" && homeContent?.hero_title) || t("hero_title")}
            </h1>
            <p className="mt-5 text-slate-300 text-base sm:text-lg max-w-xl leading-relaxed">
              {(lang === "it" && homeContent?.hero_subtitle) || t("hero_subtitle")}
            </p>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <a href="#finder" data-testid={HOME.ctaFind} className="btn-cta rounded-lg px-6 py-3.5 text-sm inline-flex items-center justify-center gap-2">
                {t("cta_find")} <ArrowRight className="w-4 h-4" />
              </a>
              <Link to="/catalogo" data-testid={HOME.ctaCatalog} className="rounded-lg px-6 py-3.5 text-sm border border-white/15 hover:border-xd-cyan text-white inline-flex items-center justify-center transition-colors">
                {t("cta_catalog")}
              </Link>
            </div>

            <div className="mt-8 grid grid-cols-3 gap-4 max-w-md">
              {[
                { icon: ShieldCheck, label: t("trust_warranty") },
                { icon: Truck, label: t("trust_shipping") },
                { icon: MessageCircle, label: t("trust_wa") },
              ].map((tr, i) => (
                <div key={i} className="flex items-center gap-2 text-xs text-slate-300">
                  <tr.icon className="w-4 h-4 text-xd-cyan" />
                  <span>{tr.label}</span>
                </div>
              ))}
            </div>
          </div>

          <div id="finder" className="animate-fade-up">
            <VehicleFinder />
          </div>
        </div>
      </section>

      {/* KLARNA BANNER */}
      <div className="pt-8">
        <KlarnaBanner variant="hero" />
      </div>

      {/* BRANDS GRID */}
      <BrandsGrid />
      <TopModelsGrid />

      {/* WHY */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16 lg:py-20">
        <div className="mb-10">
          <div className="font-mono-tech text-xs uppercase tracking-widest text-xd-cyanHover">{t("why_kicker")}</div>
          <h2 className="font-display text-3xl sm:text-4xl font-semibold text-xd-text mt-1">{t("section_why")}</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {WHY_KEYS.map((w, i) => (
            <div key={i} className="bg-white border border-xd-border rounded-xl p-6 hover:border-xd-cyan transition-colors">
              <div className="w-11 h-11 rounded-lg bg-xd-cyan/10 flex items-center justify-center mb-4">
                <w.icon className="w-5 h-5 text-xd-cyanHover" />
              </div>
              <div className="font-display font-semibold text-xd-text mb-1">{t(`${w.tKey}_title`)}</div>
              <div className="text-sm text-xd-muted leading-relaxed">{t(`${w.tKey}_desc`)}</div>
            </div>
          ))}
        </div>
      </section>

      {/* BEST SELLERS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="font-mono-tech text-xs uppercase tracking-widest text-xd-cyanHover">{t("best_kicker")}</div>
            <h2 className="font-display text-3xl font-semibold text-xd-text mt-1">{t("section_best")}</h2>
          </div>
          <Link to="/catalogo?sort=popular" className="text-sm font-medium text-xd-cyanHover hover:underline hidden sm:inline-flex items-center gap-1">
            {t("see_all")} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {best.map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
      </section>

      {/* SALE */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="font-mono-tech text-xs uppercase tracking-widest text-xd-orange">{t("sale_kicker")}</div>
            <h2 className="font-display text-3xl font-semibold text-xd-text mt-1">{t("section_sale")}</h2>
          </div>
          <Link to="/catalogo?badge=sale" className="text-sm font-medium text-xd-orange hover:underline hidden sm:inline-flex items-center gap-1">
            {t("see_all_sale")} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {sale.map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-xd-dark text-white py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="mb-10">
            <div className="font-mono-tech text-xs uppercase tracking-widest text-xd-cyan">{t("how_kicker")}</div>
            <h2 className="font-display text-3xl sm:text-4xl font-semibold mt-1">{t("section_how")}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {STEP_KEYS.map((s, i) => (
              <div key={i} className="relative bg-xd-dark2 border border-xd-darkBorder rounded-xl p-7">
                <div className="font-mono-tech text-xl text-xd-cyan">{s.n}</div>
                <div className="mt-4 font-display text-xl font-semibold">{t(`${s.tKey}_title`)}</div>
                <div className="mt-2 text-slate-400 text-sm leading-relaxed">{t(`${s.tKey}_desc`)}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* REVIEWS — auto-scrolling carousel; cards link to the source review when set */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16 lg:py-20" data-testid="home-reviews-carousel">
        <div className="mb-10 flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="font-mono-tech text-xs uppercase tracking-widest text-xd-cyanHover">{t("reviews_kicker")}</div>
            <h2 className="font-display text-3xl font-semibold text-xd-text mt-1">{t("section_reviews")}</h2>
          </div>
          {reviews.length > 3 && (
            <div className="text-xs text-xd-muted font-mono-tech">
              {reviews.length} recensioni verificate
            </div>
          )}
        </div>
        {reviews.length === 0 ? (
          <div className="text-center text-xd-muted text-sm py-10">Le recensioni compariranno presto — stiamo raccogliendo i feedback dei clienti.</div>
        ) : (
          <>
            <div className="relative overflow-hidden reviews-carousel-mask">
              <div
                className="flex gap-5 animate-reviews-scroll"
                style={{ width: "max-content" }}
                data-testid="reviews-carousel-track"
              >
                {[...reviews, ...reviews].map((r, idx) => {
                  const meta = REVIEW_SOURCES[r.source] || null;
                  const Wrap = r.source_url ? "a" : "article";
                  const wrapProps = r.source_url ? { href: r.source_url, target: "_blank", rel: "noreferrer" } : {};
                  return (
                    <Wrap
                      key={`${r.id}-${idx}`}
                      {...wrapProps}
                      data-testid={`review-card-${r.id}-${idx}`}
                      className={`w-[300px] sm:w-[360px] shrink-0 bg-white rounded-2xl border border-xd-border p-6 flex flex-col ${r.source_url ? "hover:border-xd-cyan/60 hover:shadow-md transition-all cursor-pointer" : ""}`}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex gap-0.5">
                          {Array.from({ length: r.rating || 5 }).map((_, i) => (
                            <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
                          ))}
                        </div>
                        {meta && (
                          <span className={`inline-flex items-center gap-1 text-[10px] font-semibold text-white rounded-full px-2 py-0.5 ${meta.badge}`}>
                            {meta.icon}
                            {meta.label}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-xd-text leading-relaxed flex-1 line-clamp-6">&ldquo;{r.text}&rdquo;</p>
                      <div className="mt-4 flex items-center gap-3">
                        {r.customer_avatar ? (
                          <img src={r.customer_avatar} alt={r.customer_name} className="w-9 h-9 rounded-full object-cover" loading="lazy" />
                        ) : (
                          <div className="w-9 h-9 rounded-full bg-xd-cyan/10 text-xd-cyanHover font-semibold flex items-center justify-center text-sm">
                            {(r.customer_name || "?").slice(0, 1).toUpperCase()}
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-semibold text-xd-text truncate">{r.customer_name}</div>
                          {r.created_at && (
                            <div className="text-[10px] text-xd-muted font-mono-tech uppercase tracking-widest">
                              {new Date(r.created_at).toLocaleDateString("it-IT", { month: "short", year: "numeric" })}
                            </div>
                          )}
                        </div>
                        {r.source_url && <ArrowRight className="w-4 h-4 text-xd-cyanHover shrink-0" />}
                      </div>
                    </Wrap>
                  );
                })}
              </div>
            </div>
            <div className="mt-6 flex items-center justify-center gap-3 flex-wrap">
              <a
                href={REVIEWS_FACEBOOK_URL}
                target="_blank"
                rel="noreferrer"
                data-testid="reviews-view-fb"
                className="inline-flex items-center gap-2 text-xs font-semibold text-white bg-[#1877F2] hover:bg-[#0f5fce] transition-colors rounded-full px-4 py-2"
              >
                <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="currentColor" aria-hidden><path d="M22 12a10 10 0 1 0-11.6 9.9v-7H8V12h2.4V9.8c0-2.4 1.4-3.7 3.6-3.7 1 0 2.1.2 2.1.2v2.3h-1.2c-1.2 0-1.5.7-1.5 1.5V12h2.6l-.4 2.9h-2.2V22A10 10 0 0 0 22 12z"/></svg>
                Leggi tutte su Facebook
              </a>
              <a
                href={REVIEWS_TRUSTPILOT_URL}
                target="_blank"
                rel="noreferrer"
                data-testid="reviews-view-tp"
                className="inline-flex items-center gap-2 text-xs font-semibold text-white bg-[#00B67A] hover:bg-[#009964] transition-colors rounded-full px-4 py-2"
              >
                <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="currentColor" aria-hidden><path d="M12 2l2.6 7.6h8L16.1 14l2.6 7.6L12 17l-6.7 4.6L7.9 14l-6.5-4.4h8L12 2z"/></svg>
                Leggi tutte su Trustpilot
              </a>
            </div>
          </>
        )}
      </section>

      {/* LATEST BLOG POSTS */}
      {latestPosts.length > 0 && (
        <section className="bg-xd-dark text-white" data-testid="home-latest-blog">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 lg:py-20">
            <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
              <div>
                <div className="font-mono-tech text-xs uppercase tracking-widest text-xd-cyan">Blog · Guide & News</div>
                <h2 className="font-display text-3xl sm:text-4xl font-semibold mt-1">Dal blog di audrivio</h2>
              </div>
              <Link to="/blog" data-testid="home-blog-view-all" className="text-sm text-xd-cyanHover hover:text-white inline-flex items-center gap-1">
                Vedi tutti gli articoli <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {latestPosts.map((p) => (
                <Link
                  key={p.id}
                  to={`/blog/${p.slug || p.id}`}
                  data-testid={`home-blog-post-${p.id}`}
                  className="group bg-xd-dark2 border border-xd-darkBorder rounded-2xl overflow-hidden hover:border-xd-cyan/40 transition-colors flex flex-col"
                >
                  <div className="aspect-[16/10] bg-xd-dark overflow-hidden">
                    {p.cover_image ? (
                      <img src={p.cover_image} alt={p.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-xd-cyan/10 to-xd-orange/10" />
                    )}
                  </div>
                  <div className="p-5 flex-1 flex flex-col">
                    {p.category && <div className="font-mono-tech text-[10px] uppercase tracking-widest text-xd-cyanHover mb-2">{p.category}</div>}
                    <h3 className="font-display text-lg font-semibold text-white leading-tight line-clamp-2 group-hover:text-xd-cyanHover transition-colors">{p.title}</h3>
                    {p.excerpt && <p className="text-sm text-slate-400 mt-2 line-clamp-3 flex-1">{p.excerpt}</p>}
                    <div className="mt-4 text-xs font-mono-tech text-slate-500 uppercase tracking-widest inline-flex items-center gap-1">
                      {t("blog_read")} <ArrowRight className="w-3 h-3" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}
